import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useLanguage } from "@/lib/i18n";
import { api } from "@/lib/api";
import { ArrowRight, Building2, Package, Clock, Shield, Ruler, Wrench, Users, CheckCircle, FileText, PenTool, UsersRound, HardHat, Factory, TowerControl, Settings, ClipboardCheck, KeyRound } from "lucide-react";
import SEO from "@/components/SEO";

export default function DeveloperSpaPage() {
  const { t, isRTL } = useLanguage();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.getProducts().then(setProducts).catch(() => {});
  }, []);

  const services = [
    {
      icon: Building2,
      title: t("developer.projectPlanning"),
      description: t("developer.projectPlanningDesc")
    },
    {
      icon: Package,
      title: t("developer.bulkSupply"),
      description: t("developer.bulkSupplyDesc")
    },
    {
      icon: Ruler,
      title: t("developer.customSpecs"),
      description: t("developer.customSpecsDesc")
    },
    {
      icon: Clock,
      title: t("developer.timeline"),
      description: t("developer.timelineDesc")
    },
    {
      icon: Wrench,
      title: t("developer.installation"),
      description: t("developer.installationDesc")
    },
    {
      icon: Shield,
      title: t("developer.warranty"),
      description: t("developer.warrantyDesc")
    }
  ];

  const projectStats = [
    { value: "500+", label: t("developer.unitsSupplied") },
    { value: "8+", label: t("developer.developerProjects") },
    { value: "12", label: t("developer.yearsExperience") }
  ];

  const faqs = [
    {
      question: t("developer.faq1Q") || "What is the typical lead time for bulk orders?",
      answer: t("developer.faq1A") || "Lead times vary based on order size and customization requirements. Standard models typically require 4-6 weeks for production, while custom specifications may require 8-12 weeks. We work closely with your project timeline to ensure delivery aligns with your construction schedule."
    },
    {
      question: t("developer.faq2Q") || "Can you match specific design requirements for our development?",
      answer: t("developer.faq2A") || "Yes, we offer customization options including shell colors, cabinet finishes, jet configurations, and control panel specifications. For larger projects, we can also accommodate custom dimensions within our manufacturing capabilities."
    },
    {
      question: t("developer.faq3Q") || "How do you coordinate with our construction schedule?",
      answer: t("developer.faq3A") || "We assign a dedicated project coordinator who works with your site team. Units can be manufactured in advance and stored, with phased delivery matching your floor-by-floor or phase-by-phase handover schedule."
    },
    {
      question: t("developer.faq4Q") || "What technical support do you provide to our MEP contractors?",
      answer: t("developer.faq4A") || "We provide detailed technical drawings, electrical requirements, plumbing specifications, and structural load calculations. Our technical team is available for site visits and can provide training for your MEP contractors."
    },
    {
      question: t("developer.faq5Q") || "Do you offer warranty programs for developer projects?",
      answer: t("developer.faq5A") || "Yes, we offer extended warranty programs tailored for developers, including transferable warranties to end buyers, dedicated support hotlines for property management, and annual maintenance packages."
    }
  ];

  return (
    <div data-testid="developer-services-page">
      <SEO 
        title="Developer Services for Real Estate Projects | Pruva Jacuzzi"
        description="Engineering and manufacturing support for developers including balcony plunge pools, custom spa systems, acrylic panels, and low power jacuzzi solutions."
        keywords="developer spa solutions, bulk jacuzzi supply dubai, contractor spa systems, hospitality jacuzzi, residential development spa, wholesale hot tub UAE, commercial spa supplier, project spa installation"
        canonical={typeof window !== 'undefined' ? `${window.location.origin}/custom-solutions/developer-services` : "https://www.pruvajacuzzi.com/custom-solutions/developer-services"}
        breadcrumbs={[
          { name: "Home", url: typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com" },
          { name: "Custom Solutions", url: typeof window !== 'undefined' ? `${window.location.origin}/custom-solutions` : "https://www.pruvajacuzzi.com/custom-solutions" },
          { name: "Developer Services", url: typeof window !== 'undefined' ? `${window.location.origin}/custom-solutions/developer-services` : "https://www.pruvajacuzzi.com/custom-solutions/developer-services" }
        ]}
        faq={faqs}
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24" style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)" }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-amber-400 font-medium mb-3">
                {t("developer.heroLabel")}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6">
                {t("developer.heroTitle")}
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                {t("developer.heroDesc")}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
                {projectStats.map((stat) => (
                  <div key={stat.label}>
                    <div className="text-2xl font-semibold text-amber-400">{stat.value}</div>
                    <div className="text-sm text-slate-400">{stat.label}</div>
                  </div>
                ))}
              </div>
              <div className={`flex flex-wrap gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Link to="/contact" className="bg-white text-slate-800 px-8 py-3.5 text-sm uppercase tracking-widest font-medium hover:bg-slate-100 transition-colors">
                  {t("developer.requestQuote")}
                </Link>
                <Link to="/spa-series" className="border border-white/50 text-white px-8 py-3.5 text-sm uppercase tracking-widest font-medium hover:bg-white/10 transition-colors">
                  {t("developer.viewProducts")}
                </Link>
              </div>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src="/images/optimized/developer.webp"
                alt="Developer spa solutions for residential projects"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TurnKey Solutions Highlight */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-amber-400 font-medium mb-3">{t("developer.completeDelivery") || "Complete Project Delivery"}</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-4">
              {t("developer.turnkeyTitle") || "TurnKey Solutions — From Design to Handover"}
            </h2>
            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
              {t("developer.turnkeyDesc") || "We manage every aspect of your spa and balcony plunge pool installation, ensuring seamless coordination with your project team and delivering ready-to-use wellness systems to your buyers."}
            </p>
          </div>
          
          {/* TurnKey Process Steps */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              { icon: PenTool, step: "01", title: t("developer.design"), desc: t("developer.designDesc") },
              { icon: UsersRound, step: "02", title: t("developer.coordination") || "Developer Coordination", desc: t("developer.coordinationDesc") || "Direct collaboration with architects, consultants, MEP contractors, and project managers" },
              { icon: HardHat, step: "03", title: t("developer.siteMeetings") || "Site & Technical Meetings", desc: t("developer.siteMeetingsDesc") || "On-site assessments, technical reviews, and coordination with your construction team" },
              { icon: Factory, step: "04", title: t("developer.production"), desc: t("developer.productionDesc") },
              { icon: TowerControl, step: "05", title: t("developer.lifting") || "Lifting Operations", desc: t("developer.liftingDesc") || "Specialized crane and lifting services for balcony and rooftop installations in high-rise buildings" },
              { icon: Wrench, step: "06", title: t("developer.installation"), desc: t("developer.installationDesc") },
              { icon: ClipboardCheck, step: "07", title: t("developer.testCommission") || "Test & Commissioning", desc: t("developer.testCommissionDesc") || "Comprehensive system testing, calibration, and performance verification before handover" },
              { icon: KeyRound, step: "08", title: t("developer.handover"), desc: t("developer.handoverDesc") }
            ].map((item, i) => (
              <div key={item.step} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 hover:bg-white/10 transition-colors">
                <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-10 h-10 bg-amber-400/20 rounded-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-amber-400" />
                  </div>
                  <span className="text-2xl font-bold text-amber-400/50">{item.step}</span>
                </div>
                <h3 className="text-lg font-medium text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <div className={`inline-flex items-center gap-3 bg-amber-400/20 border border-amber-400/30 px-6 py-3 rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
              <CheckCircle className="w-5 h-5 text-amber-400" />
              <span className="text-amber-200 font-medium">{t("developer.onePartner") || "One Partner, Complete Solution — No Coordination Hassle for Developers"}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Civil Defense Approved Installation Banner */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-emerald-900 via-emerald-800 to-emerald-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        </div>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border-2 border-white/30">
                <svg className="w-10 h-10 md:w-12 md:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
            <div className="flex-1 text-center lg:text-start">
              <span className="inline-block px-3 py-1 bg-white/20 text-white text-xs uppercase tracking-widest font-medium rounded-full mb-3">
                {t("home.civilDefenseBadge")}
              </span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-3">
                {t("home.civilDefenseTitle")}
              </h2>
              <p className="text-emerald-100 text-base md:text-lg max-w-3xl">
                {t("home.civilDefenseText")}
              </p>
            </div>
            <div className="flex-shrink-0 hidden xl:block">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <svg className="w-5 h-5 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t("home.civilDefenseFeature1")}
                </div>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <svg className="w-5 h-5 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t("home.civilDefenseFeature2")}
                </div>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <svg className="w-5 h-5 text-emerald-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {t("home.civilDefenseFeature3")}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3">{t("developer.servicesTitle")}</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
              {t("developer.servicesSubtitle") || "End-to-End Developer Support"}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div key={service.title} className="p-6 bg-slate-50 border border-slate-100 hover:border-slate-300 transition-colors">
                <service.icon className="w-8 h-8 text-amber-700 mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-slate-900 mb-2">{service.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why TurnKey */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3">{t("developer.whyTurnkey") || "Why Choose TurnKey"}</p>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-6">
                {t("developer.simplifiedExecution") || "Simplified Project Execution"}
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                {t("developer.turnkeyApproach") || "With Pruva's TurnKey approach, developers work with a single point of contact for the entire spa installation process. We handle coordination with all parties, eliminating the complexity of managing multiple contractors."}
              </p>
              <div className="space-y-4">
                {[
                  t("developer.benefit1") || "Single contract covering design through handover",
                  t("developer.benefit2") || "Dedicated project manager for your development",
                  t("developer.benefit3") || "Direct coordination with your MEP and construction teams",
                  t("developer.benefit4") || "Crane and lifting logistics handled by us",
                  t("developer.benefit5") || "Quality assurance at every stage",
                  t("developer.benefit6") || "Warranty documentation ready for unit handover"
                ].map((item) => (
                  <div key={item} className={`flex items-start gap-3 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="aspect-square overflow-hidden">
              <img 
                src="/images/optimized/custom-3.webp"
                alt="TurnKey balcony plunge pool installation"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Product Range */}
      <section className="py-16 md:py-24 bg-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-amber-400 font-medium mb-3">{t("developer.productRange") || "Product Range"}</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight">
              {t("developer.systemsTitle") || "Systems for Every Project Type"}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-700/50 p-8 border border-slate-600">
              <h3 className="text-xl font-medium mb-4">{t("products.spaSeries")}</h3>
              <p className="text-slate-300 mb-6">{t("developer.spaDesc") || "Large-format systems for villa rooftops, penthouses, and premium residential units."}</p>
              <ul className="space-y-2 text-sm text-slate-400 mb-6">
                <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><CheckCircle className="w-4 h-4 text-amber-400" /> {t("developer.spaFeature1") || "6-8 person capacity"}</li>
                <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><CheckCircle className="w-4 h-4 text-amber-400" /> {t("developer.spaFeature2") || "Up to 116 hydro jets"}</li>
                <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><CheckCircle className="w-4 h-4 text-amber-400" /> {t("developer.spaFeature3") || "Premium hydrotherapy features"}</li>
              </ul>
              <Link to="/spa-series" className={`inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-medium ${isRTL ? 'flex-row-reverse' : ''}`}>
                {t("developer.viewSpaSeries") || "View Spa Series"} <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
            </div>
            <div className="bg-slate-700/50 p-8 border border-slate-600">
              <h3 className="text-xl font-medium mb-4">{t("products.jacuzziSeries")}</h3>
              <p className="text-slate-300 mb-6">{t("developer.jacuzziDesc") || "Compact systems for apartment balconies, terraces, and space-efficient installations."}</p>
              <ul className="space-y-2 text-sm text-slate-400 mb-6">
                <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><CheckCircle className="w-4 h-4 text-amber-400" /> {t("developer.jacuzziFeature1") || "2-4 person capacity"}</li>
                <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><CheckCircle className="w-4 h-4 text-amber-400" /> {t("developer.jacuzziFeature2") || "Optimized load distribution"}</li>
                <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><CheckCircle className="w-4 h-4 text-amber-400" /> {t("developer.jacuzziFeature3") || "Ideal for high-rise units"}</li>
              </ul>
              <Link to="/jacuzzi-series" className={`inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-medium ${isRTL ? 'flex-row-reverse' : ''}`}>
                {t("developer.viewJacuzziSeries") || "View Jacuzzi Series"} <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3">{t("developer.faqLabel") || "FAQ"}</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900">
              {t("developer.faqTitle")}
            </h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white p-6 border border-slate-100">
                <h3 className="text-lg font-medium text-slate-900 mb-3">{faq.question}</h3>
                <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-6">
                {t("developer.ctaTitle2") || "Let's Discuss Your Project"}
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                {t("developer.ctaDesc2") || "Whether you're planning a single villa or a 200-unit development, our team is ready to provide a tailored solution. Contact us for a project consultation and volume pricing."}
              </p>
              <div className={`flex flex-wrap gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <Link to="/contact" className="bg-slate-800 text-white px-8 py-3.5 text-sm uppercase tracking-widest font-medium hover:bg-slate-700 transition-colors">
                  {t("developer.requestQuote")}
                </Link>
                <a href="tel:+971542184086" className="border border-slate-300 text-slate-800 px-8 py-3.5 text-sm uppercase tracking-widest font-medium hover:bg-slate-50 transition-colors">
                  {t("developer.callUs") || "Call +971 54 218 4086"}
                </a>
              </div>
            </div>
            <div className="bg-slate-50 p-8 border border-slate-100">
              <h3 className={`text-lg font-medium text-slate-900 mb-4 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <FileText className="w-5 h-5 text-amber-700" />
                {t("developer.infoPack") || "Request Information Pack"}
              </h3>
              <p className="text-sm text-slate-600 mb-6">
                {t("developer.infoPackDesc") || "Get our developer information pack including product catalogs, technical specifications, and case studies."}
              </p>
              <ul className="space-y-2 text-sm text-slate-600 mb-6">
                <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><CheckCircle className="w-4 h-4 text-green-600" /> {t("developer.packItem1") || "Full product catalog PDF"}</li>
                <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><CheckCircle className="w-4 h-4 text-green-600" /> {t("developer.packItem2") || "Technical drawings & specs"}</li>
                <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><CheckCircle className="w-4 h-4 text-green-600" /> {t("developer.packItem3") || "Project case studies"}</li>
                <li className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><CheckCircle className="w-4 h-4 text-green-600" /> {t("developer.packItem4") || "Volume pricing guide"}</li>
              </ul>
              <Link to="/contact" className={`inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 text-sm font-medium ${isRTL ? 'flex-row-reverse' : ''}`}>
                {t("developer.requestPack") || "Request Pack"} <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
